import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import ArticleCard from "@/components/ArticleCard";
import NewsletterSignup from "@/components/NewsletterSignup";

const CATEGORIES = [
  "All Insights",
  "Denials",
  "Revenue Recovery",
  "Revenue Forecasting",
  "KPIs & Analytics",
  "Payor Performance",
  "AI & Automation",
  "Leadership",
  "Operational Excellence",
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch { return ""; }
}

function readingMinutes(text) {
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

export default function InsightsHome() {
  const [params, setParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeCat = params.get("category") || "All Insights";
  const activeTag = params.get("tag") || "";
  const q = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const cat = activeCat !== "All Insights" ? activeCat : undefined;
    const fetchList = api.get("/articles", {
      params: { category: cat, tag: activeTag || undefined, q: q || undefined, limit: 30 },
    });
    const fetchFeatured = !activeTag && !q && activeCat === "All Insights"
      ? api.get("/articles", { params: { limit: 1 } })
      : Promise.resolve({ data: { items: [] } });

    Promise.all([fetchList, fetchFeatured])
      .then(([listRes, featRes]) => {
        if (!alive) return;
        setArticles(listRes.data.items || []);
        // pick featured: explicit featured if any, else latest
        const items = featRes.data.items || [];
        setFeatured(items[0] || null);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [activeCat, activeTag, q]);

  const setCategory = (c) => {
    const p = new URLSearchParams(params);
    if (c === "All Insights") p.delete("category"); else p.set("category", c);
    p.delete("tag");
    setParams(p);
  };

  const onSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (searchInput) p.set("q", searchInput); else p.delete("q");
    setParams(p);
  };

  const showHero = !activeTag && !q && activeCat === "All Insights" && featured;
  const gridList = showHero ? articles.filter((a) => a.slug !== featured.slug) : articles;

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative bg-[var(--rciq-navy)] text-white overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern opacity-50 pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[var(--rciq-teal)] opacity-[0.08] blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-20 md:pt-28 md:pb-28">
          <div className="eyebrow eyebrow-light">Revenue Cycle IQ · Executive Publication</div>
          <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.95] font-700 mt-5 tracking-tight">
            Insights
          </h1>
          <p className="mt-5 text-xl md:text-2xl text-white max-w-4xl font-500 leading-snug">
            Revenue Cycle Intelligence, Industry Trends &amp; Performance Strategies
          </p>
          <p className="mt-5 text-base md:text-lg text-white/75 max-w-3xl leading-relaxed">
            Explore expert insights, industry trends, and practical strategies to improve
            financial performance, reduce denials, strengthen collections, optimize workflows,
            and drive operational excellence across the revenue cycle.
          </p>

          <form onSubmit={onSearch} className="mt-10 flex items-center gap-3 max-w-xl" data-testid="hero-search-form">
            <div className="relative flex-1">
              <svg className="absolute left-5 top-1/2 -translate-y-1/2 text-white/50" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                data-testid="hero-search-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search insights by keyword..."
                className="w-full bg-white/10 border border-white/15 rounded-full pl-12 pr-5 py-3.5 text-sm placeholder-white/50 focus:outline-none focus:border-[var(--rciq-teal)]"
              />
            </div>
            <button type="submit" className="btn-pill btn-pill-primary text-[12px]" data-testid="hero-search-submit">Search</button>
          </form>
        </div>
      </section>

      {/* Featured article hero */}
      {showHero && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20" data-testid="featured-hero">
          <div className="eyebrow mb-6">Featured Insight</div>
          <Link to={`/article/${featured.slug}`} className="group block" data-testid={`featured-article-${featured.slug}`}>
            <div className="grid md:grid-cols-5 gap-8 lg:gap-12 items-center">
              <div className="md:col-span-3 rounded-2xl overflow-hidden bg-[var(--rciq-bg-tint)] aspect-[16/10]">
                <img
                  src={featured.cover_image || "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80"}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
              </div>
              <div className="md:col-span-2">
                <span className="tag-pill">{featured.category}</span>
                <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.05] font-700 text-[var(--rciq-ink)] group-hover:text-[var(--rciq-teal-dark)] transition-colors mt-4">
                  {featured.title}
                </h2>
                <p className="mt-5 text-[var(--rciq-ink-2)] leading-relaxed text-base md:text-lg">
                  {featured.excerpt}
                </p>
                <div className="mt-5 flex items-center gap-4 text-xs text-[var(--rciq-muted)] font-600">
                  <span>{formatDate(featured.published_at)}</span>
                  <span>·</span>
                  <span>{featured.reading_time || readingMinutes(featured.content || featured.excerpt)} min read</span>
                </div>
                <span className="mt-7 inline-flex items-center gap-2 btn-pill btn-pill-primary text-[12px]">
                  Read Article
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                  </svg>
                </span>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Category filters */}
      <section className="border-y border-[var(--rciq-line)] mt-20 bg-white sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 overflow-x-auto" data-testid="filter-bar">
          <div className="flex items-center gap-2 min-w-max">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`text-[12px] uppercase tracking-[0.12em] font-700 px-4 py-2 rounded-full transition whitespace-nowrap ${
                  activeCat === c
                    ? "bg-[var(--rciq-ink)] text-white"
                    : "text-[var(--rciq-muted)] hover:text-[var(--rciq-ink)]"
                }`}
              >
                {c}
              </button>
            ))}
            {activeTag && (
              <button
                onClick={() => { const p = new URLSearchParams(params); p.delete("tag"); setParams(p); }}
                data-testid="clear-tag-filter"
                className="ml-2 tag-pill hover:bg-[var(--rciq-teal)] hover:text-white transition"
              >
                #{activeTag} ✕
              </button>
            )}
            {q && (
              <button
                onClick={() => { setSearchInput(""); const p = new URLSearchParams(params); p.delete("q"); setParams(p); }}
                data-testid="clear-search"
                className="ml-2 tag-pill hover:bg-[var(--rciq-teal)] hover:text-white transition"
              >
                "{q}" ✕
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="eyebrow">Latest</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-700 mt-3 leading-tight tracking-tight">
              {q ? `Results for "${q}"` : activeTag ? `#${activeTag}` : activeCat}
            </h2>
          </div>
          {gridList.length > 0 && (
            <div className="text-xs text-[var(--rciq-muted)] font-600 hidden md:block">
              {gridList.length} article{gridList.length === 1 ? "" : "s"}
            </div>
          )}
        </div>

        {loading ? (
          <div data-testid="loading-state" className="text-[var(--rciq-muted)] py-20 text-center">Loading insights…</div>
        ) : gridList.length === 0 ? (
          <div data-testid="empty-state" className="py-20 text-center">
            <p className="text-[var(--rciq-muted)]">No insights match your filters.</p>
            <button onClick={() => setParams(new URLSearchParams())} className="mt-4 btn-pill btn-pill-ghost text-[12px]">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="article-grid">
            {gridList.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
          </div>
        )}
      </section>

      {/* Newsletter */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <NewsletterSignup variant="dark" />
      </section>

      {/* Footer CTA */}
      <section className="bg-[var(--rciq-bg-tint)] border-t border-[var(--rciq-line)]" data-testid="footer-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 md:py-28 text-center">
          <div className="eyebrow justify-center" style={{ display: "inline-flex" }}>Partner With Us</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] font-700 mt-5 tracking-tight">
            Ready to Improve Revenue Cycle Performance?
          </h2>
          <p className="mt-6 text-lg text-[var(--rciq-ink-2)] max-w-3xl mx-auto leading-relaxed">
            Explore Revenue Cycle IQ's consulting services, analytics solutions, and executive
            intelligence tools designed to help healthcare organizations improve financial outcomes.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://www.therevenuecycleiq.com/book-a-call" target="_blank" rel="noopener noreferrer"
              className="btn-pill btn-pill-primary text-[12px]"
              data-testid="cta-schedule-consultation"
            >
              Schedule a Consultation
            </a>
            <a
              href="https://www.therevenuecycleiq.com/services" target="_blank" rel="noopener noreferrer"
              className="btn-pill btn-pill-ghost text-[12px]"
              data-testid="cta-explore-solutions"
            >
              Explore Solutions
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
